import { useRef, useState, type ChangeEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/Button'
import { Alert } from '@/components/ui/Alert'
import { useImportStudents } from '../hooks/useInstitution'

/**
 * Import CSV d'élèves (spécifications § 9.2). L'import étant partiel, le bilan
 * détaille chaque ligne rejetée avec son numéro : l'administrateur corrige son
 * fichier au lieu de deviner.
 */
export function StudentImportZone() {
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)
  const [fileName, setFileName] = useState('')
  const importStudents = useImportStudents()

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]

    if (!file) return

    setFileName(file.name)
    importStudents.mutate(file)
    event.target.value = ''
  }

  const result = importStudents.data

  return (
    <section aria-labelledby="import-title" className="flex flex-col gap-4">
      <div>
        <h2 id="import-title" className="font-display text-lg font-semibold text-on-surface">
          {t('institution.import.title')}
        </h2>
        <p className="mt-1 text-sm text-on-surface-variant">{t('institution.import.columns')}</p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <input
          ref={inputRef}
          type="file"
          accept=".csv,text/csv"
          onChange={handleChange}
          aria-label={t('institution.import.file_input')}
          className="sr-only"
          id="institution-import-file"
        />
        <Button onClick={() => inputRef.current?.click()} isLoading={importStudents.isPending}>
          {t('institution.import.choose_file')}
        </Button>
        {fileName && <span className="text-sm text-on-surface-variant">{fileName}</span>}
      </div>

      {importStudents.isError && <Alert variant="error">{t('institution.import.failed')}</Alert>}

      {result && (
        <div className="flex flex-col gap-3">
          <Alert variant={result.failed === 0 ? 'success' : 'warning'}>
            {t('institution.import.summary', { imported: result.imported, failed: result.failed })}
          </Alert>

          {result.errors.length > 0 && (
            <ul className="flex flex-col gap-1 rounded-2xl bg-surface-low p-4 text-sm">
              {result.errors.map((error) => (
                <li key={`${error.line}-${error.email ?? ''}`} className="text-on-surface-variant">
                  {t('institution.import.line_error', {
                    line: error.line,
                    email: error.email ?? '—',
                    reason: error.error,
                  })}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  )
}
